import { useState, useRef, ReactNode } from "react";
import {
  FiChevronDown,
  FiChevronRight,
  FiClock,
  FiEdit,
  FiHelpCircle,
  FiSettings,
} from "react-icons/fi";
import { get, isEmpty } from "lodash";
import { getDomain } from "../utils/isUrl";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  useApplicationContext,
  PersonaType,
} from "../context/ApplicationContext";
import { NavBar } from "../components/NavBar";
import { createPortal } from "react-dom";
import { useAppContext } from "../context/AppContext";
import classNames from "classnames";
import { canClaimOrganization } from "../context/utils/claims";
import { getMenuForPersona } from "./menus";
import { SYSTEM_USER_TYPES } from "../types";

// Menu item types
export interface MenuItem {
  menuLabel: string | ReactNode;
  icon?: ReactNode;
  items?: SubMenuItem[];
}

export interface SubMenuItem {
  to: string;
  menuLabel: string | ReactNode;
}

interface ApplicationLayoutProps {
  children: ReactNode;
  menuItems?: MenuItem[]; // Optional - will use persona-based menu if not provided
}

const LoadingElement = () => {
  return (
    <div className="bg-white py-2 px-4 flex items-center gap-2.5 h-[60px] animate-pulse">
      <div className="w-10 h-10 rounded-full bg-gray-200"></div>
      <div className="flex-1 flex">
        <div className="flex flex-col gap-1 flex-1">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    </div>
  );
};

const ClaimSelectorMenu = ({
  claims,
  currentOrgId,
  onSelect,
  position,
  onMouseEnter,
  onMouseLeave,
}: {
  claims: any[];
  currentOrgId: string;
  onSelect: (orgId: string) => void;
  position: { top: number; left: number };
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  return createPortal(
    <div
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 min-w-[200px]"
      style={{ top: position.top, left: position.left }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {claims.map((claim) => {
        const orgId = get(claim, "organization.id", "");
        const isActive = orgId === currentOrgId;

        return (
          <button
            key={orgId}
            onClick={() => onSelect(orgId)}
            className={`w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
              isActive ? "bg-nav-secondary" : ""
            }`}
          >
            <img
              src={get(claim, "organization.profile_image_url", "")}
              className="w-8 h-8 rounded-full object-contain border bg-white p-0.5"
              alt={get(claim, "organization.name", "")}
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                const img = e.currentTarget;
                img.onerror = null;
                img.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%23e5e7eb'/%3E%3C/svg%3E";
              }}
            />
            <div className="text-left">
              <p className="font-medium text-sm">
                {get(claim, "organization.name", "")}
              </p>
              <p className="text-xs text-gray-500">
                {getDomain(get(claim, "organization.socials.website", ""))}
              </p>
            </div>
            {isActive && <span className="ml-auto text-nav-bg">✓</span>}
          </button>
        );
      })}
    </div>,
    document.body,
  );
};

const UserInfoDisplay = () => {
  const { user } = useApplicationContext();

  return (
    <div className="bg-white py-2 px-4 flex items-center gap-2.5 h-[60px]">
      <div className="w-10 h-10 rounded-full bg-nav-bg text-nav-text flex items-center justify-center font-semibold">
        {get(user, "first_name", "U").charAt(0).toUpperCase()}
      </div>
      <div className="flex flex-col flex-1">
        <p className="font-semibold truncate">
          {get(user, "first_name", "")} {get(user, "other_names", "")}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {get(user, "email", "")}
        </p>
      </div>
    </div>
  );
};

const SideClaimElement = () => {
  const { current, claims, setCurrentOrganization } = useApplicationContext();
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const elementRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringMenu = useRef(false);
  const navigate = useNavigate();

  // Show loading state while data is being populated
  if (isEmpty(current) || isEmpty(claims)) {
    return <LoadingElement />;
  }

  const clearHoverTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearHoverTimeout();
    if (claims.length > 1 && elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top,
        left: rect.right + 10,
      });
      setShowMenu(true);
    }
  };

  const handleMouseLeave = () => {
    clearHoverTimeout();
    timeoutRef.current = setTimeout(() => {
      if (!isHoveringMenu.current) {
        setShowMenu(false);
      }
    }, 100);
  };

  const handleMenuMouseEnter = () => {
    isHoveringMenu.current = true;
    clearHoverTimeout();
  };

  const handleMenuMouseLeave = () => {
    isHoveringMenu.current = false;
    clearHoverTimeout();
    timeoutRef.current = setTimeout(() => {
      setShowMenu(false);
    }, 100);
  };

  const handleSelectOrganization = (orgId: string) => {
    setCurrentOrganization(orgId);
    setShowMenu(false);
    isHoveringMenu.current = false;
  };

  return (
    <div
      ref={elementRef}
      className="bg-white py-2 px-4 flex items-center gap-2.5 h-[60px] relative cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={get(current, "organization.profile_image_url", "")}
        className="w-10 h-10 rounded-full object-contain border bg-white p-1"
        alt={get(current, "organization.name", "")}
        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
          // Silently fail
        }}
      />
      <div className="flex-1 flex group">
        <div className="flex flex-col">
          <p className="font-semibold flex gap-1 items-center truncate overflow-ellipsis">
            {get(current, "organization.name", "")}{" "}
            {get(current, "status", -1) === 0 && <FiClock />}
            {get(current, "status", -1) === 1 && (
              <span className="group-hover:block hidden">
                <FiEdit />
              </span>
            )}
          </p>
          <p className="text-xs">
            {getDomain(get(current, "organization.socials.website", ""))}
          </p>
        </div>
      </div>
      {claims?.length > 1 && (
        <div>
          <button className={showMenu ? "hidden" : "block"}>
            <FiChevronDown />
          </button>
          <button className={showMenu ? "block" : "hidden"}>
            <FiChevronRight />
          </button>
        </div>
      )}

      {showMenu && claims.length > 1 && (
        <ClaimSelectorMenu
          claims={claims}
          currentOrgId={get(current, "organization.id", "")}
          onSelect={handleSelectOrganization}
          position={menuPosition}
          onMouseEnter={handleMenuMouseEnter}
          onMouseLeave={handleMenuMouseLeave}
        />
      )}
    </div>
  );
};

const SideEntityElement = () => {
  const { persona } = useApplicationContext();
  const hasClaimFeature = canClaimOrganization(persona);

  if (hasClaimFeature) {
    return <SideClaimElement />;
  } else {
    return <UserInfoDisplay />;
  }
};

const SideNavigationSettings = () => {
  return (
    <div className="w-full py-8">
      <button className="w-full flex px-4 py-1.5 items-center">
        <FiHelpCircle />
        <span className="px-2 text-left grow items-center">Help Center</span>
      </button>
      <Link to="/settings" className="w-full flex px-4 py-1.5 items-center">
        <FiSettings />
        <span className="px-2 text-left grow items-center">
          Account Settings
        </span>
      </Link>
    </div>
  );
};

const SideNavigation = ({ menuItems }: { menuItems: MenuItem[] }) => {
  const { sidebarOpen, setSidebarOpen } = useAppContext();

  return (
    <nav
      className={classNames({
        "bg-bs-light-gray-50 flex flex-col font-medium text-sm divide-y-2 divide-gray-200 overflow-y-auto h-full border-r border-solid": true,
        "bg-gray-50 h-[calc(100vh-60px)] md:h-[calc(100vh-60px)] z-20": true,
        "min-w-[275px] w-screen sm:max-w-[275px] grow": true,
        "hidden xl:block": !sidebarOpen,
        "block fixed xl:relative ": sidebarOpen,
      })}
    >
      <SideEntityElement />
      <div className="w-full flex-grow divide-y-2 divide-gray-200 flex flex-col">
        {menuItems.map((i) => {
          return (
            <div
              key={String(i.menuLabel)}
              className="w-full flex flex-col py-1.5"
            >
              <button className="w-full flex px-4 py-1.5 items-center font-medium text-base">
                <span className="px-0 text-left grow items-center">
                  {i.menuLabel}
                </span>
                {i?.items && i?.items.length > 0 && <FiChevronDown />}
              </button>
              {i?.items && i?.items.length > 0 && (
                <div className="flex flex-col gap-1 px-2">
                  {i.items.map((e) => {
                    return (
                      <NavLink
                        to={e.to}
                        className={({ isActive }) =>
                          `font-normal px-6 py-0.5 rounded-md transition-colors ${
                            isActive
                              ? "bg-nav-bg text-nav-text"
                              : "hover:bg-nav-bg hover:text-nav-text"
                          }`
                        }
                        key={e.to}
                        onClick={() => {
                          setSidebarOpen(false);
                        }}
                      >
                        <span>{e.menuLabel}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <SideNavigationSettings />
    </nav>
  );
};

export const ApplicationLayout = ({
  children,
  menuItems: propsMenuItems,
}: ApplicationLayoutProps) => {
  const { user, persona } = useApplicationContext();

  // Use provided menuItems or auto-select based on persona
  const menuItems = propsMenuItems || getMenuForPersona(persona);

  return (
    <div className="flex flex-col h-screen ">
      <NavBar />
      <div className="flex flex-1 pt-[60px] overflow-hidden">
        <SideNavigation menuItems={menuItems} />

        <main className="flex-1 overflow-y-auto">
          {user && !user.has_active_subscription && (
            <div className="px-4 pt-3">
              <div className="bg-nav-secondary border-nav-active border rounded-md px-4 py-2 flex items-center gap-4 flex-wrap">
                <p>
                  You're currently on our free plan with limited features.
                  Upgrade to access unlimited content, advanced tools, and
                  exclusive content.
                </p>
                {persona === SYSTEM_USER_TYPES.FOUNDER && (
                  <NavLink
                    to="/upgrade"
                    className="bg-button-bg text-button-text px-4 py-1 rounded-md text-sm"
                  >
                    Upgrade to Premium
                  </NavLink>
                )}
                {persona !== SYSTEM_USER_TYPES.FOUNDER && (
                  <NavLink
                    to="https://meetings-eu1.hubspot.com/meetings/dario-giuliani/briter-intelligence-book-a-demo"
                    className="bg-button-bg text-button-text px-4 py-1 rounded-md text-sm"
                  >
                    Book a demo
                  </NavLink>
                )}
              </div>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};
